import React, { useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import FormControl from '@material-ui/core/FormControl';
import IconButton from '@material-ui/core/IconButton';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';

import { withStyles } from '@material-ui/core/styles';
import { FormHelperText } from '@material-ui/core';

import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import Typography from 'Components/UI/Typography';

import { partition } from 'Helpers/Utils';
import { createBaseStyles } from './styles';

const styles = theme => ({
  root: {
    ...createBaseStyles(theme),
  },
  inputLabel: {},
  selectRoot: {
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  },
  selectIcon: {},
  menuItem: {},
  helperText: {},
  groupedOption: {
    paddingLeft: theme.spacing(3),
  },
  groupedOptionHidden: {
    display: 'none',
  },
  menuHeader: {
    display: 'flex',
    alignItems: 'center',
    height: theme.spacing(6),
    paddingRight: theme.spacing(2),
    cursor: 'default',
  },
  menuHeaderText: {
    pointerEvents: 'none',
  },
  clearGroupIconButton: {},
  groupHeaderMenuItem: {
    display: 'flex',
    paddingRight: theme.spacing(1),
  },
  groupHeaderText: {
    flexGrow: 1,
    pointerEvents: 'none',
  },
  setGroupIcon: {
    pointerEvents: 'none',
    marginLeft: theme.spacing(2),
    color: theme.palette.facet.primaryGray,
  },
});

const NO_CLOSE = '__false__'; // This must be a string because it is being used as a data value on a DOM node.

const shouldCloseMenu = v => v !== NO_CLOSE;

function GroupedSelectControl({
  className,
  classes,
  error,
  helperText,
  label,
  options,
  onChange,
  selectId,
  value,
  variant,
  shouldDisableOption,
  LabelProps,
  SelectProps,
  ...rest
}) {
  const [open, setOpen] = useState(false);
  // Initial value is set to the group of the selected value
  const [selectedGroup, setSelectedGroup] = useState(options.reduce((acc, opt) => ((opt.value === value) ? opt.group : acc), null));
  let selectLabelProps = {};

  if (label) {
    selectLabelProps = {
      label,
      labelId: `${selectId}-label`,
    };
  }

  const grouplessOptionValues = {};

  const groups = Object.keys(options.reduce((acc, opt) => {
    if (opt.group) {
      return { ...acc, [opt.group]: true };
    }

    // Add the option as a groupless option
    grouplessOptionValues[opt.value] = true;

    return acc;
  }, {}));

  const [grouplessOptions, otherOptions] = partition(options, (opt) => {
    return grouplessOptionValues[opt.value];
  });

  const opts = (selectedGroup)
    ? options.map(opt => ({ ...opt, hidden: opt.group !== selectedGroup }))
    : [
      ...groups.map(groupName => ({
        groupHeader: true,
        groupName,
      })),
      ...grouplessOptions,
      ...otherOptions.map(opt => ({ ...opt, hidden: true })), // Keep all items in the list but make them invisible.
    ];

  return (
    <FormControl
      error={error}
      className={classNames(classes.root, className)}
      variant={variant}
      {...rest}
    >
      {label && (
        <InputLabel
          className={classes.inputLabel}
          htmlFor={selectId}
          id={`${selectId}-label`}
          {...LabelProps}
        >
          {label}
        </InputLabel>
      )}
      <Select
        classes={{
          root: classes.selectRoot,
          icon: classes.selectIcon,
        }}
        id={selectId}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={(evt) => {
          if (shouldCloseMenu(evt.target.dataset.close)) {
            setOpen(false);
            setSelectedGroup(null);
          }
        }}
        open={open}
        renderValue={(selectedValue) => {
          const option = options.find(v => (v.value === selectedValue));

          if (option.group) {
            return `${option.group} > ${option.name}`;
          }

          return option.name;
        }}
        value={value}
        onChange={null}
        {...selectLabelProps}
        {...SelectProps}
      >
        {selectedGroup ? (
          <div
            className={classes.menuHeader}
            data-close={NO_CLOSE}
          >
            <IconButton
              className={classes.clearGroupIconButton}
              color="primary"
              onClick={(evt) => {
                evt.stopPropagation();
                setSelectedGroup(null);
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Typography
              className={classes.menuHeaderText}
              color="primary"
              variant="h6"
            >
              {selectedGroup}
            </Typography>
          </div>
        ) : null}
        {opts.map((option, i) => {
          if (option.groupHeader) {
            return (
              <MenuItem
                className={classes.groupHeaderMenuItem}
                color="primary"
                data-close={NO_CLOSE}
                key={`header.${i}`} // eslint-disable-line react/no-array-index-key
                onClick={(evt) => {
                  evt.stopPropagation();
                  setSelectedGroup(option.groupName);
                }}
              >
                <Typography
                  className={classes.groupHeaderText}
                >
                  {option.groupName}
                </Typography>
                <ChevronRightIcon className={classes.setGroupIcon} />
              </MenuItem>
            );
          }

          return (
            <MenuItem
              className={classNames(classes.menuItem, {
                [classes.groupedOption]: !!option.group,
                [classes.groupedOptionHidden]: option.hidden,
              })}
              key={`${selectedGroup || 'no-group'}.${option.value}`}
              onClick={() => {
                onChange({ target: { value: option.value } });
              }}
              value={option.value}
              disabled={shouldDisableOption(option.value)}
            >
              {option.name}
            </MenuItem>
          );
        })}
      </Select>
      {helperText && (
        <FormHelperText className={classes.helperText}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}

GroupedSelectControl.defaultProps = {
  className: '',
  label: null,
  variant: 'standard',
  shouldDisableOption: () => false,
};

GroupedSelectControl.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  selectId: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]).isRequired,
  variant: PropTypes.oneOf(['filled', 'outlined', 'standard']),
  label: PropTypes.string,
  shouldDisableOption: PropTypes.func,
};

export default withStyles(styles, { name: 'GroupedSelectControl' })(GroupedSelectControl);
